import { isEmpty } from 'lodash';
import { useRef, useState } from 'react';
import { Button, MessagePlugin, TableRowData, Tag } from 'tdesign-react';
import { v4 } from 'uuid';

import { FormTitle } from '@/components/form/materials/Title';
import DialogForm from '@/components/form/wrapper/DialogForm';
import SimpleSortTable from '@/components/table/simpleSortTable';
import { HomeDataManagerType } from '@/const/promotion';
import { IStory } from '@/interface';
import { INovelHomeItem, NovelHomeItemType } from '@/interface/novel';
import { SpaceCard } from '@/pages/promotion/cards/spaceCard';
import { StoryCard } from '@/pages/promotion/cards/storyCard';
import { getNovelHomeItemDetail, modifyNovelHomeItemDetail } from '@/services/novel';

import { formKeys } from '../config';
import { formConfig, NovelHomeTypeOptions } from './form-config/common';
import styles from './index.module.less';
import { validateMultiCollections } from './validate';

interface IProps {
  data: INovelHomeItem[],
  onChange: (data: Partial<INovelHomeItem>) => void;
  onMove: (id: string) => void;
  onSort: (data: INovelHomeItem[]) => void;
}
const MAX_TABLE_WIDTH = 1200;

const List: React.FC<IProps> = (props) => {
  const {
    data = [],
    onChange,
    onMove,
    onSort,
  } = props;
  const tableRef = useRef(null);
  const formRef = useRef<any>();
  const columns = [
    {
      colKey: 'type',
      title: '类型',
      width: MAX_TABLE_WIDTH * 0.05,
      cell: ({ row }: { row: TableRowData }) => {
        const cname = NovelHomeTypeOptions?.find(item => item.value === row?.type)?.label;
        return (
          <div><Tag theme="primary">{cname}</Tag></div>
        );
      },
    },
    {
      colKey: 'name',
      title: '标题',
      width: MAX_TABLE_WIDTH * 0.1,
      cell: ({ row }: { row: TableRowData }) => {
        const name = row?.[formKeys.name];
        return (
          <div>{name || '--'} </div>
        );
      },
    },
    {
      colKey: 'cover',
      title: '合集类型',
      width: MAX_TABLE_WIDTH * 0.15,
      minWidth: 200,
      cell: ({ row }: { row: TableRowData }) => {
        if (row?.managerType === HomeDataManagerType.HOMEDATAMANAGERTYPE_COLLECTION
          || row?.managerType === HomeDataManagerType.HOMEDATAMANAGERTYPE_RANK) {
          return <SpaceCard key={row?.id} data={row?.space?.opt || row?.space} />;
        }
        return '--';
      },
    },
    {
      colKey: 'novels',
      title: '选择小说',
      width: MAX_TABLE_WIDTH * 0.35,
      cell: ({ row }: { row: TableRowData }) => {
        const items = row?.novels || [];
        if (isEmpty(items)) {
          return '--';
        }
        return (
          <div className={styles.stories}>
            {items.map((item: Partial<IStory>) => (
              <StoryCard key={item.id} data={item} />
            ))}
          </div>
        );
      },
    },
    {
      colKey: 'op',
      title: '操作',
      width: MAX_TABLE_WIDTH * 0.1,
      align: 'center' as 'center',
      cell: ({ row }: { row: TableRowData }) => (
        <div className={styles.options}>
          <Button
            onClick={() => handleEdit(row)}
            theme="default"
            variant="text"
          >
            编辑
          </Button>
          <Button
            theme="default"
            variant="text"
            onClick={() => onMove(row.id)}
          >
            移除
          </Button>
        </div>
      ),
    },
  ];

  const handleEdit = async (row: TableRowData) => {
    let rowData = { ...row };
    if (row?.id) {
      const resData = await fetchDetail(row.id);
      rowData = resData;
    }
    setFormData(rowData as INovelHomeItem);
    setVisible(true);
  };

  const fetchDetail = async (id: string) => {
    try {
      const fetchItemDetail = getNovelHomeItemDetail;
      const res = await fetchItemDetail(id);
      return res;
    } catch (error: any) {
      MessagePlugin.error({
        content: error.message || '获取失败，请重试',
      });
      return {};
    }
  };

  const getData = (data: INovelHomeItem[]) => {
    onSort(data);
  };

  const [visible, setVisible] = useState(false);
  const [formData, setFormData] = useState<Partial<INovelHomeItem>>({});

  const onSubmit = async () => {
    if (formData.type === NovelHomeItemType.HOMEDATAITEMTYPE_MULTICOLLECTION) {
      const valid = validateMultiCollections(formData.multiCollection);
      if (!valid) {
        return false;
      }
    }

    try {
      const res = await modifyNovelHomeItemDetail(formData);
      onChange({ ...formData, id: res });
      MessagePlugin.success({
        content: '保存成功',
      });
      return true;
    } catch (error: any) {
      MessagePlugin.error({
        content: error.message || '保存失败，请重试',
      });
      return false;
    }
  };

  const handleClose = () => {
    setFormData({});
    setVisible(false);
  };

  const onValuesChange = (content: Partial<INovelHomeItem>) => {
    const postData = { ...formData, ...content };
    setFormData(postData);
  };

  const handleAddItem = () => {
    setVisible(true);
    setFormData({ type: NovelHomeItemType.HOMEDATAITEMTYPE_MULTICOLLECTION });
  };

  const addItem = () => (
    <div className={styles.addWrap}>
      <Button style={{ width: '100%', height: 40 }} variant="outline" theme="primary" ghost onClick={handleAddItem}>
        新增配置
      </Button>
    </div>
  );

  return (
    <>
      <SimpleSortTable
        cRef={tableRef}
        columnsConfig={columns}
        dataList={data}
        dragWidth={100}
        getData={getData as any}
        extendsConfig={{}}
        tableConfig={{ bottomContent: <>{addItem()}</> }}
      />
      <DialogForm
        cRef={formRef}
        fieldsConfig={formConfig[formData.type || NovelHomeItemType.HOMEDATAITEMTYPE_MULTICOLLECTION]}
        onSubmit={onSubmit}
        onClose={handleClose}
        isVisible={visible}
        formData={{
          ...formData,
          multiCollection: (formData?.multiCollection || []).map((item: any) => ({
            ...item,
            id: item.id || v4(),
          })),
        }}
        formConfig={{
          layout: 'vertical',
          labelWidth: 120,
          labelAlign: 'top',
          onValuesChange,
        }}
        layOutConfig={{
          paddingBottom: 30,
          maxHeight: '75vh',
        }}
        dialogConfig={{
          header: (
            <FormTitle
              title={'新增配置'}
              replaceObject={{}}
            />
          ),
          style: { width: '90%' },
          destroyOnClose: true,
        }}
      />
    </>
  );
};

export default List;
